/* Neat Digital — custom mega menu behaviour
 * Desktop: hover/focus opens scoped dropdown; intent delay; ESC closes.
 * Mobile : full-screen overlay; slide-in sub-panels; lock body scroll; close on ESC.
 */
(function () {
  const ROOT_SELECTOR = '[data-neat-menu]';
  const OPEN_INTENT_MS = 60;
  const CLOSE_INTENT_MS = 120;

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const isMobile = root.dataset.neatVariant === 'mobile';
      if (isMobile) {
        // bindMobile self-guards its event listeners but always runs the
        // dedupe/portal pass so it stays correct across Horizon's section
        // hydration (which can re-insert a duplicate overlay into the root).
        bindMobile(root);
      } else {
        if (root.dataset.neatBound === '1') return;
        root.dataset.neatBound = '1';
        bindDesktop(root);
      }
    });
  }

  /* ---------- Desktop ---------- */
  function bindDesktop(root) {
    const items = root.querySelectorAll('[data-neat-item]');
    let openTimer = null;
    let closeTimer = null;
    let activeItem = null;

    const close = (item) => {
      if (!item) return;
      item.removeAttribute('data-open');
      const link = item.querySelector('[data-neat-trigger]');
      if (link) link.setAttribute('aria-expanded', 'false');
      if (activeItem === item) activeItem = null;
      if (!root.querySelector('[data-neat-item][data-open]')) {
        root.removeAttribute('data-any-open');
      }
    };

    const open = (item) => {
      if (!item) return;
      // Always close other open items, even if this one has no panel —
      // hovering over a panel-less link (e.g. "Our Work") must dismiss any
      // dropdown that was open from a previous trigger.
      items.forEach((other) => { if (other !== item) close(other); });
      if (!item.querySelector('[data-neat-panel]')) return;
      item.setAttribute('data-open', '');
      const link = item.querySelector('[data-neat-trigger]');
      if (link) link.setAttribute('aria-expanded', 'true');
      activeItem = item;
      root.setAttribute('data-any-open', '');
    };

    const scheduleOpen = (item) => {
      clearTimeout(closeTimer);
      clearTimeout(openTimer);
      openTimer = setTimeout(() => open(item), OPEN_INTENT_MS);
    };
    const scheduleClose = (item) => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => close(item), CLOSE_INTENT_MS);
    };

    items.forEach((item) => {
      const trigger = item.querySelector('[data-neat-trigger]');
      const panel = item.querySelector('[data-neat-panel]');
      if (!trigger) return;

      item.addEventListener('pointerenter', () => scheduleOpen(item));
      item.addEventListener('pointerleave', () => scheduleClose(item));

      if (panel) {
        // Touch / keyboard: clicking trigger toggles
        trigger.addEventListener('click', (e) => {
          if (window.matchMedia('(hover: hover)').matches) return;
          e.preventDefault();
          if (item.hasAttribute('data-open')) close(item);
          else open(item);
        });
        trigger.addEventListener('focus', () => scheduleOpen(item));
        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) scheduleClose(item);
        });
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && activeItem) {
        const trigger = activeItem.querySelector('[data-neat-trigger]');
        close(activeItem);
        if (trigger) trigger.focus();
      }
    });

    document.addEventListener('click', (e) => {
      if (!root.contains(e.target) && activeItem) close(activeItem);
    });
  }

  /* ---------- Mobile ----------
   *
   * Horizon's <header-component> has `contain: layout style`, which acts as
   * the containing block for any position:fixed descendants and squashes the
   * overlay to the header's height. We portal the overlay onto <body> to
   * escape that containment.
   *
   * Horizon also runs section hydration that re-inserts the server-rendered
   * markup, which can re-create an orphan overlay inside the header AFTER our
   * portal runs. To stay robust:
   *   1. dedupe + portal idempotently on every init pass
   *   2. use event delegation so newly-inserted orphan triggers/buttons keep
   *      working without re-binding
   *   3. watch for orphans appearing later via MutationObserver
   */
  let mobileGlobalsBound = false;
  function ensureCanonicalOverlay() {
    const all = document.querySelectorAll('[data-neat-overlay]');
    let canonical = null;
    for (const ov of all) {
      if (ov.parentElement === document.body) { canonical = ov; break; }
    }
    if (!canonical) canonical = all[all.length - 1] || null;
    if (!canonical) return null;
    all.forEach((ov) => { if (ov !== canonical) ov.remove(); });
    if (canonical.parentElement !== document.body) document.body.appendChild(canonical);
    return canonical;
  }
  function getOverlay() {
    return document.body.querySelector(':scope > [data-neat-overlay]') || ensureCanonicalOverlay();
  }
  function openMenu() {
    const ov = ensureCanonicalOverlay();
    if (!ov) return;
    ov.setAttribute('data-open', '');
    document.documentElement.style.overflow = 'hidden';
    document.querySelectorAll('[data-neat-open]').forEach((t) => t.setAttribute('aria-expanded', 'true'));
  }
  function closeMenu() {
    const ov = getOverlay();
    if (ov) {
      ov.removeAttribute('data-open');
      ov.querySelectorAll('[data-neat-subpanel][data-open]').forEach((p) => p.removeAttribute('data-open'));
    }
    document.documentElement.style.overflow = '';
    document.querySelectorAll('[data-neat-open]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
  }

  function bindMobile(root) {
    // Always run the dedupe/portal pass — handles late re-hydration.
    ensureCanonicalOverlay();

    if (mobileGlobalsBound) return;
    mobileGlobalsBound = true;

    // Event delegation: works regardless of which trigger/button is "live"
    // after re-hydration creates duplicates.
    document.addEventListener('click', (e) => {
      const openTrig = e.target.closest('[data-neat-open]');
      if (openTrig) {
        e.preventDefault();
        const ov = getOverlay();
        if (ov && ov.hasAttribute('data-open')) closeMenu();
        else openMenu();
        return;
      }
      const closeTrig = e.target.closest('[data-neat-close]');
      if (closeTrig) {
        e.preventDefault();
        closeMenu();
        return;
      }
      const subOpen = e.target.closest('[data-neat-open-sub]');
      if (subOpen) {
        e.preventDefault();
        const id = subOpen.getAttribute('data-neat-open-sub');
        const ov = getOverlay();
        if (!ov) return;
        const panel = ov.querySelector(`[data-neat-subpanel="${CSS.escape(id)}"]`);
        if (panel) {
          panel.setAttribute('data-open', '');
          const focusTarget = panel.querySelector('[data-neat-back]');
          if (focusTarget) setTimeout(() => focusTarget.focus(), 60);
        }
        return;
      }
      const back = e.target.closest('[data-neat-back]');
      if (back) {
        e.preventDefault();
        const panel = back.closest('[data-neat-subpanel]');
        if (panel) panel.removeAttribute('data-open');
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const live = document.querySelector('[data-neat-overlay][data-open]');
      if (!live) return;
      const openPanel = live.querySelector('[data-neat-subpanel][data-open]');
      if (openPanel) openPanel.removeAttribute('data-open');
      else closeMenu();
    });

    // Watch for orphan overlays inserted after init (Horizon hydration).
    // Only act when a NEW [data-neat-overlay] appears; otherwise this fires
    // on every DOM mutation in the page.
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-neat-overlay]')) {
            ensureCanonicalOverlay();
            return;
          }
          if (node.querySelector && node.querySelector('[data-neat-overlay]')) {
            ensureCanonicalOverlay();
            return;
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-init after Shopify section render
  document.addEventListener('shopify:section:load', init);
})();
